import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { getKidSub } from '@/auth/authStore';
import { getStudyDocument, getStudyHome, organiseStudyDocument } from './studyApi';

const schema = z.object({ subject: z.string().min(1, 'Choose a subject first.') });

/** Use the existing learner-owned filing operation; never guess the PDF subject. */
export function StudyPdfTutorSetup({
  documentId,
  onReady,
}: {
  documentId: string;
  onReady: () => void;
}) {
  const owner = getKidSub();
  const context = useQuery({
    queryKey: ['study-pdf-tutor-setup', owner, documentId],
    queryFn: async () => {
      const [home, document] = await Promise.all([getStudyHome(), getStudyDocument(documentId)]);
      return { home, document };
    },
    retry: false,
  });
  const termId = context.data?.document.term_id ?? context.data?.home.current_term?.id;
  const term =
    context.data?.home.academic_years
      .flatMap((year) => year.terms)
      .find((item) => item.id === termId) ??
    context.data?.home.terms.find((item) => item.id === termId);
  const subjects =
    term?.subjects ??
    (termId === context.data?.home.current_term?.id ? (context.data?.home.subjects ?? []) : []);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { subject: '' },
  });
  const organise = useMutation({
    mutationFn: ({ subject }: z.infer<typeof schema>) => {
      const selectedTerm = termId;
      if (!selectedTerm || !subjects.some((item) => item.subject_key === subject))
        throw new Error('Choose a Term and subject in Study first.');
      return organiseStudyDocument(documentId, subject, selectedTerm);
    },
    onSuccess: onReady,
  });
  if (context.isError)
    return (
      <div className="rounded-xl bg-wash-coral p-3 text-xs text-ink">
        <p>Your subjects could not load.</p>
        <button
          type="button"
          className="btn-pill-secondary mt-2 min-h-11 text-xs"
          onClick={() => void context.refetch()}
        >
          Retry subjects
        </button>
      </div>
    );
  if (!context.data)
    return (
      <p role="status" className="text-xs text-slate2">
        Opening your subjects…
      </p>
    );
  const { home, document } = context.data;
  if (!(document.term_id ?? home.current_term?.id) || !subjects.length)
    return (
      <p className="rounded-xl bg-wash-sunshine p-3 text-xs leading-relaxed text-ink">
        Set up your Term and subjects in Study, then reopen this PDF. Your annotations stay saved.
      </p>
    );
  return (
    <form
      onSubmit={form.handleSubmit((values) => organise.mutate(values))}
      className="rounded-2xl border border-hairline bg-canvas p-3"
    >
      <label className="text-xs font-bold text-ink" htmlFor="pdf-tutor-subject">
        Which subject is this PDF for?
      </label>
      <select
        id="pdf-tutor-subject"
        {...form.register('subject')}
        disabled={organise.isPending}
        className="mt-2 block min-h-11 w-full rounded-xl border border-hairline bg-canvas-pure px-3 text-sm text-ink"
      >
        <option value="">Choose your subject</option>
        {subjects.map((subject) => (
          <option key={subject.subject_key} value={subject.subject_key}>
            {subject.display_name}
          </option>
        ))}
      </select>
      <p className="mt-2 text-[11px] leading-relaxed text-slate2">
        This files the PDF under your selected subject. Your annotations stay saved.
      </p>
      <button
        type="submit"
        disabled={organise.isPending}
        className="btn-pill-secondary mt-3 min-h-11 w-full text-xs"
      >
        {organise.isPending ? 'Saving subject…' : 'Save subject and use this page'}
      </button>
      {form.formState.errors.subject && (
        <p role="alert" className="mt-2 text-xs text-danger-600">
          {form.formState.errors.subject.message}
        </p>
      )}
      {organise.error instanceof Error && (
        <p role="alert" className="mt-2 text-xs text-danger-600">
          {organise.error.message}
        </p>
      )}
    </form>
  );
}
